import type { Project } from '../types/Project';

// Pinned projects come first (in featuredIds order) so the All Projects page
// leads with them; the homepage list below the carousel filters them out.
const projects: Project[] = [
  {
    id: 'pokeharness',
    title: "Pokéharness",
    description: "A macOS app I built to manage all my coding agents in one place. It's built around delegation: one main agent plans the work, asks a smarter model like Fable or Opus for advice before it commits to anything big, and then hands the actual coding to cheaper agents instead of doing it all itself. Every session (Claude Code, Codex or Cursor) shows up as a Pokémon walking around a pixel-art garden. It goes to a station and shows what tool it's using when it's working, waits at a signpost when it needs you, and evolves the longer it runs. Built with Electron, React and Pixi.js, with a built-in terminal to talk to each agent.",
    image: '/images/projects/pokeharness-2026-09-v3.webp',
    tags: ["Electron", "React", "TypeScript", "Pixi.js", "node-pty", "xterm.js", "Agent Orchestration", "Pixel Art"],
    links: {
      github: "https://github.com/mavericksxx/pokemon-harness",
      live: "https://pokeharness.parthkohale.com/"
    }
  },
  {
    id: 'epochs',
    title: "Epochs",
    description: "A spinning globe that shows history from 3000 BC to today, built with typesafe.ai's Jev model. You drag a timeline and real events light up on the map where they happened. There are about 15,000 events, all pulled from Wikidata. Jev scores each one for how much it's about war, politics, religion, economy, science and culture, and how big its impact was. That score decides how bright each dot is on screen. Scoring the whole set cost 44 cents. There's also an Ask Jev page where you type in any historical event. Jev scores it the same way, guesses roughly when it happened and which country it's most tied to, and pins it on the globe. A side column walks you through 17 eras of history as you scroll.",
    image: '/images/projects/epochs-2026-09.webp',
    tags: ["TypeScript", "Vite", "Typesafe Jev", "Cloudflare Workers", "Wikidata", "Data Pipeline", "Data Visualization", "Pixel Art"],
    links: {
      github: "https://github.com/mavericksxx/typesafe-jev-history-globe",
      live: "https://epochs.parthkohale.com/"
    }
  },
  {
    id: 'zenith',
    title: "Zenith",
    description: "A private tool that helps me decide what to build next. It reads my GitHub commits, READMEs and issues, keeps track of which projects I've been active on, and gives me a weekly summary plus a ranked list of ideas with reasons for each. It runs completely on Cloudflare's free tier (Workers, D1, KV, Vectorize and Cron Triggers) behind Cloudflare Access, and it only has read-only access to my GitHub except for one repo it can write to.",
    image: '/images/projects/zenith.webp',
    tags: ["Cloudflare Workers", "D1", "Vectorize", "Next.js", "TypeScript", "Hono", "Gemini", "RAG", "GitHub API"],
    links: {}
  },
  {
    id: 'flappynn',
    title: "FlappyNN",
    description: "Flappy Bird, but the birds teach themselves to play. Each bird has a small neural network, and after every generation a genetic algorithm keeps the best ones and mutates them, so they get noticeably better after a few rounds. Built with Pygame and NumPy.",
    image: '/images/projects/flappynn-2026-09.webp',
    tags: ["Python", "Pygame", "Numpy"],
    links: {
      github: "https://github.com/mavericksxx/flappynn",
      live: "https://flappynn.parthkohale.com/"
    }
  },
  {
    id: 'veralux',
    title: "VeraLux",
    description: "A fact-checking app where six AI agents (built with LangGraph) work together to verify a claim. It uses Llama 3.3 70B through Groq and Gemini 2.0 Flash, pulls evidence from Snopes, PolitiFact and FactCheck.org, and shows you what each agent is doing live on a ReactFlow graph.",
    image: '/images/projects/veralux-demo.webp',
    tags: ["FastAPI", "React", "LangGraph", "LLMs", "Multi-Agent Systems", "Web Scraping", "ReactFlow", "TypeScript"],
    links: {
      github: "https://github.com/mavericksxx/VeraLux",
      live: "https://vera-lux.vercel.app/"
    }
  },
  {
    id: 'per-app-audio',
    title: "Per-App Audio",
    description: "macOS doesn't let you change the volume or output device for individual apps, so I built a menu-bar app that does. You pick a device next to an app and its audio moves there, while everything else keeps playing on your normal output. It uses Core Audio process taps under the hood, lets you boost an app up to 200% (with a limiter so it doesn't distort), and remembers your routes after a restart or when your AirPods reconnect.",
    image: '/images/projects/per-app-audio.webp',
    tags: ["Swift", "SwiftUI", "Core Audio", "macOS", "Real-Time Audio"],
    links: {
      github: "https://github.com/mavericksxx/per-app-audio"
    }
  },
  {
    id: 'echoes',
    title: "Echoes",
    description: "A pixel-art Naruto village that changes based on what I listen to on Spotify. Each music genre is a character with its own district. The village grows, goes quiet or celebrates depending on what I've been playing. Once a day a Gemini agent changes the weather, festivals, visitors and moods by itself, and a chronicle shows what it changed and why. It also has my Wrapped stats, playlists, what I'm listening to right now, and a Hokage you can chat with. It runs on Cloudflare Workers and D1, and my Spotify token is stored encrypted, so visitors never have to log in.",
    image: '/images/projects/echoes-2026-09.webp',
    tags: ["TypeScript", "Vite", "Cloudflare Workers", "D1", "Spotify API", "Gemini", "AI Agents", "Pixel Art"],
    links: {
      github: "https://github.com/mavericksxx/echoes",
      live: "https://echoes.parthkohale.com/"
    }
  },
  {
    id: 'neuralsketch',
    title: "NeuralSketch",
    description: "You draw a rough sketch, and a neural network figures out what it is and turns it into a cleaner piece of art. It recognizes the sketch in real time as you draw. Built in Python.",
    image: '/images/projects/NeuralSketch.webp',
    tags: ["Python", "Neural Networks", "Machine Learning", "Computer Vision", "HTML"],
    links: {
      github: "https://github.com/mavericksxx/NeuralSketch",
      live: "/"
    }
  },
  {
    id: 'Ink&Insight',
    title: "Ink&Insight",
    description: "A Flask app that compares PDF documents to see how similar they are. It uses Google Cloud Vision and Mathpix to pull out the text and equations, and BERT to compare the content, writing style and flow.",
    image: '/images/projects/Ink&Insight.webp',
    tags: ["Python", "NLTK", "Flask", "Torch", "BERT", "Google Cloud Vision", "Mathpix"],
    links: {
      github: "https://github.com/mavericksxx/ink-and-insight",
      live: "/"
    }
  },
  {
    id: 'nnvis',
    title: "NNVIS",
    description: "A web app that lets you watch neural networks evolve in real time. Flask runs the simulation, D3.js draws each network's structure and weights, and it keeps track of the best performers across generations.",
    image: '/images/projects/nnvis.webp',
    tags: ["Python", "Flask", "Numpy"],
    links: {
      github: "https://github.com/mavericksxx/nnvis",
      live: "https://github.com/mavericksxx/nnvis",
    }
  },
  {
    id: 'mncrft',
    title: "MNCRFT",
    description: "A small React app I made to keep track of tasks on my Minecraft server.",
    image: '/images/projects/mncrft.webp',
    tags: ["React", "Node.js", "Tailwind CSS"],
    links: {
      github: "https://github.com/mavericksxx/mncrft",
      live: "https://mncrft.netlify.app/"
    }
  }
];

export const allProjects = projects;

// Explicit order for the homepage carousel — slot 0 is what renders on page
// load, so this ordering is load-bearing, not cosmetic.
export const featuredIds = ['pokeharness', 'epochs', 'zenith', 'flappynn', 'veralux'];

const duplicateIds = featuredIds.filter((id, index) => featuredIds.indexOf(id) !== index);
if (duplicateIds.length > 0) {
  throw new Error(`featuredIds contains duplicate id(s): ${duplicateIds.join(', ')}`);
}

const missingIds = featuredIds.filter(id => !projects.some(p => p.id === id));
if (missingIds.length > 0) {
  throw new Error(`featuredIds references unknown project id(s): ${missingIds.join(', ')}`);
}
